import React, {
    useCallback,
    useEffect,
} from "react";

import {
    FiAlertCircle,
    FiClock,
    FiFileText,
    FiRefreshCw,
} from "react-icons/fi";

import {
    useDispatch,
    useSelector,
} from "react-redux";

import {
    fetchPurchaseOutstandingReport,
    purchaseOutstandingRecordsSelector,
    purchaseOutstandingSummarySelector,
    purchaseReportLoadingSelector,
    purchaseReportErrorSelector,
} from "../../../../../../../store/billing/purchaseReport/purchaseReportSlice";


const toNumber = (
    value
) =>
    Number.isFinite(
        Number(value)
    )
        ? Number(value)
        : 0;


const formatCurrency = (
    value
) =>
    new Intl.NumberFormat(
        "en-IN",
        {
            style: "currency",
            currency: "INR",
            minimumFractionDigits: 2,
        }
    ).format(
        toNumber(value)
    );


const formatDate = (
    value
) => {
    if (!value) {
        return "-";
    }

    return new Intl.DateTimeFormat(
        "en-IN",
        {
            day: "2-digit",
            month: "short",
            year: "numeric",
        }
    ).format(
        new Date(value)
    );
};


const OutstandingReport = ({
    fromDate,
    toDate,
    financialYear,
}) => {
    const dispatch =
        useDispatch();

    const records =
        useSelector(
            purchaseOutstandingRecordsSelector
        ) || [];

    const summary =
        useSelector(
            purchaseOutstandingSummarySelector
        ) || {};

    const loading =
        useSelector(
            purchaseReportLoadingSelector
        ) || {};

    const errors =
        useSelector(
            purchaseReportErrorSelector
        ) || {};


    const loadData =
        useCallback(
            () => {
                dispatch(
                    fetchPurchaseOutstandingReport({
                        asOfDate:
                            toDate,

                        financialYear,

                        page:
                            1,

                        limit:
                            200,
                    })
                );
            },
            [
                dispatch,
                fromDate,
                toDate,
                financialYear,
            ]
        );


    useEffect(
        () => {
            loadData();
        },
        [
            loadData,
        ]
    );


    return (
        <div>
            {errors.outstanding && (
                <div className="purchase-report-alert">
                    <FiAlertCircle />

                    {errors.outstanding}
                </div>
            )}


            <section className="purchase-report-summary-grid">

                <div className="purchase-report-summary-card">
                    <span>
                        Outstanding
                    </span>

                    <strong>
                        {formatCurrency(
                            summary?.outstandingAmount
                        )}
                    </strong>

                    <small>
                        Total payable
                    </small>
                </div>


                <div className="purchase-report-summary-card">
                    <span>
                        Overdue
                    </span>

                    <strong>
                        {formatCurrency(
                            summary?.overdueAmount
                        )}
                    </strong>

                    <small>
                        Past due
                    </small>
                </div>


                <div className="purchase-report-summary-card">
                    <span>
                        Not Due
                    </span>

                    <strong>
                        {formatCurrency(
                            summary?.notDueAmount
                        )}
                    </strong>

                    <small>
                        Future payable
                    </small>
                </div>


                <div className="purchase-report-summary-card">
                    <span>
                        Overdue Bills
                    </span>

                    <strong>
                        {toNumber(
                            summary?.overdueBills
                        )}
                    </strong>

                    <small>
                        Bills past due date
                    </small>
                </div>

            </section>


            <section className="purchase-report-table-card">

                {loading.outstanding &&
                    records.length === 0 ? (
                    <div className="purchase-report-empty">
                        <FiRefreshCw className="purchase-report-spinner" />

                        <strong>
                            Loading outstanding...
                        </strong>
                    </div>
                ) : records.length === 0 ? (
                    <div className="purchase-report-empty">
                        <FiFileText />

                        <strong>
                            No outstanding purchase bills
                        </strong>
                    </div>
                ) : (
                    <div className="purchase-report-table-wrapper">

                        <table className="purchase-report-table">

                            <thead>
                                <tr>
                                    <th>
                                        Bill
                                    </th>

                                    <th>
                                        Supplier
                                    </th>

                                    <th>
                                        Bill Date
                                    </th>

                                    <th>
                                        Due Date
                                    </th>

                                    <th className="text-right">
                                        Bill Total
                                    </th>

                                    <th className="text-right">
                                        Debit Note
                                    </th>

                                    <th className="text-right">
                                        Paid
                                    </th>

                                    <th className="text-right">
                                        Outstanding
                                    </th>

                                    <th>
                                        Age
                                    </th>
                                </tr>
                            </thead>


                            <tbody>
                                {records.map(
                                    (
                                        item,
                                        index
                                    ) => (
                                        <tr
                                            key={
                                                item?._id ||
                                                index
                                            }
                                        >
                                            <td>
                                                <strong className="purchase-report-bill-number">
                                                    {item?.documentNumber || "-"}
                                                </strong>
                                            </td>

                                            <td>
                                                {item?.supplier?.supplierName || "-"}
                                            </td>

                                            <td>
                                                {formatDate(
                                                    item?.documentDate
                                                )}
                                            </td>

                                            <td>
                                                {formatDate(
                                                    item?.dueDate
                                                )}
                                            </td>

                                            <td className="text-right">
                                                {formatCurrency(
                                                    item?.grandTotal
                                                )}
                                            </td>

                                            <td className="text-right">
                                                {formatCurrency(
                                                    item?.debitNoteAmount
                                                )}
                                            </td>

                                            <td className="text-right">
                                                {formatCurrency(
                                                    item?.paidAmount
                                                )}
                                            </td>

                                            <td className="text-right">
                                                <strong className="purchase-report-outstanding">
                                                    {formatCurrency(
                                                        item?.outstandingAmount
                                                    )}
                                                </strong>
                                            </td>

                                            <td>
                                                <span
                                                    className={
                                                        item?.overdue
                                                            ? "purchase-report-overdue-badge"
                                                            : "purchase-report-not-due-badge"
                                                    }
                                                >
                                                    <FiClock />

                                                    {item?.overdue
                                                        ? `${toNumber(
                                                            item?.daysOverdue
                                                        )} days`
                                                        : "Not due"}
                                                </span>
                                            </td>
                                        </tr>
                                    )
                                )}
                            </tbody>

                        </table>

                    </div>
                )}

            </section>
        </div>
    );
};


export default OutstandingReport;